import { useTranslation } from 'react-i18next'
import type { QuebonReviewListItem } from '@/api/quebonReviews'
import starIcon from '@/assets/reviews/star.svg'
import arrowIcon from '@/assets/reviews/icon-arrow-right-blue.svg'

interface Props {
  items: QuebonReviewListItem[]
  onSelect: (id: number) => void
}

export default function BestReviewMarquee({ items, onSelect }: Props) {
  const { t } = useTranslation('common')

  const renderCard = (
    item: QuebonReviewListItem,
    keyPrefix: string,
    idx: number,
  ) => (
    <button
      type="button"
      key={`${keyPrefix}-${idx}`}
      onClick={() => onSelect(item.storyId)}
      className="
        shrink-0 mx-6 w-[280px] h-[376px] md:w-[296px] md:h-[380px]
        rounded-[24px] overflow-hidden bg-white shadow-[0_8px_20px_0_#dadaf1]
        flex flex-col text-left
      "
    >
      <div className="w-full h-[156px] md:h-[164px]">
        <img
          src={item.thumbnail}
          alt={item.bestTitle || item.title}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="px-4 md:px-6 pt-4">
        <strong
          className="block text-[18px] md:text-[20px] text-[#171717] font-bold leading-snug"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {item.bestTitle || item.title}
        </strong>
        <p
          className="mt-4 text-[16px] text-[#505050]"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
          dangerouslySetInnerHTML={{
            __html: item.bestText || item.preview,
          }}
        />
      </div>
      <div className="mt-auto px-4 pb-4 md:px-6 md:pb-6 h-10 flex items-center justify-between">
        <ul className="flex items-center ml-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <li
              key={i}
              className="w-7 h-7 md:w-8 md:h-8 -ml-2 bg-center bg-no-repeat bg-contain"
              style={{ backgroundImage: `url(${starIcon})` }}
            />
          ))}
        </ul>
        <span className="flex items-center gap-2 text-[#4599ff] font-medium">
          {t('reviews.best.detail')}
          <img src={arrowIcon} alt="" className="w-4 h-4" />
        </span>
      </div>
    </button>
  )

  return (
    <section id="best-reviews" data-marketing-section="" className="bg-[#f9f9ff] py-12 md:py-[100px]">
      <p className="text-center text-[28px] md:text-[40px] font-bold leading-[1.4] tracking-[-0.6px]">
        <span className="md:hidden whitespace-pre-line">
          {'수학머리는\n타고나는 것이아니라\n설계하는 것'}
        </span>
        <span className="hidden md:inline">
          {t('reviews.best.sectionTitleBefore')}
        </span>
        <br />
        <span className="text-[#4599ff]">
          <span className="md:hidden whitespace-pre-line">
            {'깨봉이라면\n누구나 가능합니다.'}
          </span>
          <span className="hidden md:inline">
            {t('reviews.best.sectionTitleEmphasis')}
          </span>
        </span>
      </p>

      {items.length > 0 && (
        <div className="relative mt-10 md:mt-[88px] h-[440px] md:h-[546px]">
          <div
            aria-hidden
            className="hidden md:block absolute left-1/2 top-1/2 w-[528px] h-[528px] rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none"
            style={{ backgroundColor: '#4599ff', opacity: 0.1 }}
          />
          <div className="relative h-full overflow-hidden">
            <div
              aria-hidden
              className="absolute left-0 top-0 h-full w-[40px] md:w-[20%] z-10 pointer-events-none"
              style={{
                background:
                  'linear-gradient(90deg, #f9f9ff 0%, rgba(249,249,255,0) 100%)',
              }}
            />
            <div
              aria-hidden
              className="absolute right-0 top-0 h-full w-[40px] md:w-[20%] z-10 pointer-events-none"
              style={{
                background:
                  'linear-gradient(270deg, #f9f9ff 0%, rgba(249,249,255,0) 100%)',
              }}
            />
            <div className="relative h-full flex items-center">
              <div
                className="flex animate-review-marquee hover:[animation-play-state:paused]"
                style={{ width: 'max-content' }}
              >
                {items.map((item, idx) => renderCard(item, 'a', idx))}
                {items.map((item, idx) => renderCard(item, 'b', idx))}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
